import {assert, object, nullable, optional, integer, string, array, boolean, number, enums} from 'superstruct';

export const DateStruct = object({
    success: optional(boolean()),
    calendarConfiguration: object({
        minSelectableDays: number(),
        maxSelectableDays: number(),
        minSelectableDates: enums([1,2]),
        maxSelectableDates: enums([1,2]),
        minDate: string(),
        maxDate: string(),
        selectableValidities: array(number())
    }),
    calendarDates: array(object({
        month: number(),
        year: number(),
        dates: array(
            object({
                disabled: optional(boolean()),
                id: number(),
                date: string(),
                priceTendency: optional(enums(["low","rather-low","normal","rather-high","high"])),
                season: enums(["preseason","midseason","highseason"])
            })
        )
    }))
});


export const ActivityDateStruct = object({
    success: optional(boolean()),
    id: string(),
    calendarConfiguration: object({
        minSelectableDays: number(),
        maxSelectableDays: number(),
        minSelectableDates: enums([1,2]),
        maxSelectableDates: enums([1,2]),
        minDate: string(),
        maxDate: string()
    }),
    basePriceInfo: object({
        label: string(),
        price: number(),
    }),
    calendarDates: array(object({
        month: number(),
        year: number(),
        dates: array(
            object({
                date: string(),
                disabled: boolean(),
                soldOut: boolean(),
                availability: object({
                    status: string(),
                    total: number(),
                })
            })
        )
    }))
});

export const PriceCalculatorDateStruct = object({
    success: optional(boolean()),
    calendarConfiguration: object({
        minSelectableDays: number(),
        maxSelectableDays: number(),
        minSelectableDates: enums([1,2]),
        maxSelectableDates: enums([1,2]),
        minDate: string(),
        maxDate: string(),
        selectableValidities: array(number())
    }),
    calendarDates: array(object({
        month: number(),
        year: number(),
        dates: array(
            object({
                disabled: optional(boolean()),
                id: optional(number()),
                date: string(),
                season: enums(["preseason","midseason","highseason"]),
                priceTendency: optional(enums(["low","rather-low","normal","rather-high","high"])),
            })
        )
    }))
});

export const ActivityDateListStruct = object({
    success: optional(boolean()),
    hasAgeGroups: boolean(),
    id: string(),
    priceGroups: array(object({
        id: string(),
        label: string(),
        info: optional(string()),
        subtitle: optional(string())
    })),
    dateList: array(object({
        title: string(),
        date: string(),
        availability: object({
            total: number(),
            status: string()
        }),
        max: number(),
        priceByPriceGroup: array(object({
            id: string(),
            price: number(),
            priceBeforeDiscount: optional(number()),
            priceDiscountInfo: optional(string()),
            priceGroupInfo: optional(string())
        }))
    }))
});

export const ActivityTimeListStruct = object({
    success: optional(boolean()),
    hasAgeGroups: boolean(),
    id: string(),
    priceGroups: array(object({
        id: string(),
        label: string(),
        info: optional(string()),
        subtitle: optional(string())
    })),
    availability: object({
        total: number(),
        status: string()
    }),
    timeList: array(object({
        title: string(),
        time: string(),
        max: number(),
        isAvailable: boolean(),
        showTime: optional(boolean()),
        priceByPriceGroup: array(object({
            id: string(),
            price: number(),
            priceBeforeDiscount: optional(number()),
            priceDiscountInfo: optional(string()),
            priceGroupInfo: optional(string())
        }))
    }))
});

export const BaseConfigStruct = object({
    title: string(),
    ticketId: string(),
    firstView: enums(["calendar", "services", "persons"]),
    hasServiceView: boolean()
});

export const ActivityBaseConfigStruct = object({
    title: string(),
    activityId: string(),
    firstView: enums(["calendar", "date-list"]),
    isTimeList: optional(boolean()),
});

export const BasePriceStruct = object({
    success: optional(boolean()),
    id: string(),
    basePriceInfo: optional(object())
});

export const PriceTendencyStruct = object({
    success: optional(boolean()),
    data: object({
        percentage: optional(number()),
        percentageDescription: optional(string()),
        percentageDescriptionInfo: optional(string()),
        additionalPriceInfo: optional(string()),
        priceTendency: optional(enums(["low","rather-low","normal","rather-high","high"])),
        hidePriceTendency: optional(boolean())
    })
});

export const PriceTendencyPriceStruct = object({
    success: optional(boolean()),
    data: object({
        pricePerAdult: number(),
        hideServiceView: optional(boolean()),
        presalesExpiredMessage: optional(string()),
        noAvailableDates: optional(boolean()),
    })
});


export const ServicesStruct = object({
    success: optional(boolean()),
    serviceDescription: string(),
    options: array(object({
        id: string(),
        label: string(),
        labelSubText: optional(string()),
        info: optional(string()),
        upgrades: array(string()),
        insurances: array(string())
    })),
    upgrades: array(object({
        id: string(),
        label: string(),
        labelSubText: optional(string()),
        info: optional(string()),
        warning: optional(string())
    })),
    insurances: array(object({
        id: string(),
        label: string(),
        labelSubText: optional(string()),
        info: optional(string()),
        isNone: optional(boolean())
    }))
});

export const PricingStruct = object({
    success: optional(boolean()),
    isAgeGroup: optional(boolean()),
    priceGroups: array(object({
        id: string(),
        label: string(),
        labelPlural: string(),
        info: optional(string()),
        subtitle: optional(string())
    })),
    priceConfigurations: array(object({
        upgradeOption: integer(),
        upgradeCatalog: integer(),
        priceGroupMap: object(),
        option: string(),
        upgrades: array(string()),
        insurance: nullable(string()),
        priceByPriceGroup:  array(object({
            id: string(),
            price: number(),
            priceBeforeDiscount: optional(number()),
            priceDiscountInfo: optional(string()),
            priceGroupInfo: optional(string()),
            priceGroupWarning: optional(string()),
        }))
    }))
});

export const PriceCalculatorStruct = object({
    success: optional(boolean()),
    interests: object({
        type: string(),
        title: string(),
        data: array(object({
            id: string(),
            label: string(),
            labelSubText: optional(string()),
            categories: array(string())
        }))
    }),
    categories: object({
        type: string(),
        title: string(),
        data: array(object({
            id: string(),
            label: string(),
            labelSubText: optional(string()),
        }))
    }),
    priceGroups: object({
        type: string(),
        title: string(),
        data: array(object({
           id: string(),
           label: string(),
           labelPlural: string(),
           subtitle: optional(string()),
           labelSubText: optional(string()),
        }))
    })
})

export const PriceCalculatorPriceGroupsStruct = object({
    success: optional(boolean()),
    priceGroups: object({
        type: string(),
        title: string(),
        data: array(object({
            id: string(),
            label: string(),
            labelPlural: string(),
            subtitle: optional(string()),
            labelSubText: optional(string()),
        }))
    })
})

export const PriceCalculatorTicketsStruct = object({
    success: optional(boolean()),
    data: array(object({
        catalogTitle: string(),
        tickets: array(object({
            ticketTitle: string(),
            ticketComposition: optional(string()),
            startDate: string(),
            endDate: optional(string()),
            data: optional(array(object({
              priceGroup: string(),
              price: number(),
              priceUnit: string()
            }))),
            ticketId: string(),
            isAnnualPass: boolean(),
            isNotBookable: optional(boolean()),
            pathToDetailPage: optional(string()),
            selectedOption: optional(string()),
            selectedUpgrades: optional(array(string()))
        }))
    }))
})

export const B2BSkiTicketTypeStruct = object({
    success: optional(boolean()),
    _comment: optional(string()),
    ticketTypes: array(object({
        value: number(),
        label: string(),
        dayRange: number(),
        hasServices: optional(boolean()),
    }))
})